import { getActualDateTimeFormat, getRelativeDateFormat } from '@utils/utils';
import {
  EnvelopeAllovedActions,
  EnvelopeAllowedActionsButton,
  EnvelopeAllowedActionsName,
  FlowStageState,
  DraftEnvelopeRecipientsBaseColors,
  Instance,
} from '@const/consts';

import { ILibDropdownItem, Nullable } from '@infologistics/frontend-libraries';

import {
  EnvelopeSettingsElement,
  ICardInfoSettingsArg,
  ICheckedAllowedActions,
} from '@views/envelopes/types';
import {
  IEnvelopeTasks,
  ICurrentEnvelope,
  ICurrentEnvelopeRecipient,
  DraftEnvelopeRecipientSignerType,
  DraftEnvelopeRecipientType,
  IDraftEnvelope,
  IDraftEnvelopeField,
  IDraftEnvelopePrepositionedMark,
  IDraftEnvelopeRecipient,
  IEnveloperPrepositionedMark,
  IEnvelope,
} from '@store/modules/envelopes/types';
import { ICurrentUser } from '@store/modules/user/types';
import SharedService, { ISharedCompleteTaskRequest } from '@services/shared';
import { getAutoStageRouteKey } from '@utils/routeKeys';

export const getCardInfoSettings = (
  { oguid, sender, flowStartDateTime, lastActionDateTime }: ICardInfoSettingsArg,
  locale: string
): EnvelopeSettingsElement[] => {
  const { boxEmail, user: { fullName } } = sender

  return [
    {
      title: 'envelopes:card:idDocument',
      content: oguid
    },
    {
      title: 'envelopes:card:sender',
      content: boxEmail,
      additionalContent: fullName
    },
    {
      title: 'envelopes:card:sent',
      content: getRelativeDateFormat(flowStartDateTime, locale),
      contentTitle: getActualDateTimeFormat(flowStartDateTime, locale),
    },
    {
      title: 'envelopes:card:lastChanged',
      content: getRelativeDateFormat(lastActionDateTime, locale),
      contentTitle: getActualDateTimeFormat(lastActionDateTime, locale),
    }
  ]
}

export const getRecipientsParametersSettings = (task: IEnvelopeTasks): Nullable<EnvelopeSettingsElement[]> => {
  const result: EnvelopeSettingsElement[] = []

  const { recipient, state, completedEvidences, completedSigner } = task

  const {
    isRequireSelfie,
    isRequireSms,
    declaredSigner
  } = recipient

  const isCompleted = state === FlowStageState.COMPLETED
  const signer = completedSigner ?? declaredSigner

  signer?.position &&
    result.push({
      title: 'envelope:paramsPopover:position',
      content: signer?.position
    })

    signer?.initials &&
    result.push({
      title: 'envelope:paramsPopover:initials',
      content: signer?.initials
    })

    signer?.phone &&
    result.push({
      title: 'envelope:paramsPopover:phone',
      content: signer?.phone
    })

  if (!isCompleted) {
    isRequireSms &&
      result.push({
        title: 'envelope:paramsPopover:smsConfirmation',
        additionalContent: 'envelope:paramsPopover:required'
      })

    isRequireSelfie &&
      result.push({
        title: 'envelope:paramsPopover:selfie',
        additionalContent: 'envelope:paramsPopover:required'
      })
  } else {
    if (completedEvidences) {
      const {
        isVerifiedBySms,
        phone,
        isSelfieProvided,
        geolocation
      } = completedEvidences

      isVerifiedBySms &&
        result.push({
          title: 'envelope:paramsPopover:smsConfirmation',
          icon: 'IconCheck',
          content: phone
        })

      isSelfieProvided &&
        result.push({
          title: 'envelope:paramsPopover:selfie',
          icon: 'IconCheck',
          additionalContent: 'envelope:paramsPopover:loaded',
        })

      geolocation &&
        result.push({
          title: 'envelope:paramsPopover:geolocation',
          content: geolocation,
        })
    }
  }

  return result.length ? result : null
}

export const getCurrentEnvelopeTask = (envelope?: Nullable<ICurrentEnvelope>): IEnvelopeTasks | undefined =>
  envelope?.flow
    .flatMap(({ tasks }) => tasks)
    .find(({ oguid }) => oguid === envelope.currentUserTaskOguid)

export const getSharedCompleteTaskData = (
  profile: ICurrentUser,
  envelope?: Nullable<ICurrentEnvelope>,
  comment?: Nullable<string>
): ISharedCompleteTaskRequest | null => {
  const currentTask = getCurrentEnvelopeTask(envelope)

  if (currentTask?.recipient.assignedToBoxUser) {
    return {
      comment: comment || null,
    }
  }

  const declaredSigner = currentTask?.recipient.declaredSigner
  const fullName = declaredSigner?.fullName || profile.fullName

  if (!fullName) return null

  return {
    comment: comment || null,
    signer: {
      fullName,
      initials: declaredSigner?.initials ?? profile.initials ?? null,
      position: declaredSigner?.position ?? profile.position ?? null,
    },
  }
}

export const completeEnvelopeTaskByShare = async (
  envelope: Pick<IEnvelope, 'oguid'>,
  data: ISharedCompleteTaskRequest
): Promise<void> => {
  const { data: sharedLinkOguid } = await SharedService.getSharedOguid(envelope.oguid)

  await SharedService.postCompleteTask(sharedLinkOguid, data)
}

export const isSingleEnvelopeMassActionAvailable = (
  envelope: Pick<
    IEnvelope,
    'allowedActions' | 'allowedMassActions' | 'currentUserTaskOguid'
  >,
  action: EnvelopeAllovedActions,
  instance: Instance
): boolean => Boolean(
  envelope.currentUserTaskOguid &&
  envelope.allowedActions.includes(action) &&
  envelope.allowedMassActions?.includes(action) &&
  (
    action === EnvelopeAllovedActions.APPROVE ||
    (action === EnvelopeAllovedActions.SIGN && instance === Instance.VN)
  )
)

export const checkAllowedActions = (allowedActions: EnvelopeAllovedActions[]): ICheckedAllowedActions => {
  const checkAllowedActionsIncludes = (value: EnvelopeAllovedActions): boolean =>
    allowedActions.includes(value)

  return {
    haveDeclineSign:
      checkAllowedActionsIncludes(EnvelopeAllovedActions.DECLINE_SIGN),
    haveDeclineApprove:
      checkAllowedActionsIncludes(EnvelopeAllovedActions.DECLINE_APPROVE),
    haveStopProcess:
      checkAllowedActionsIncludes(EnvelopeAllovedActions.STOP_PROCESS),
    haveSign:
      checkAllowedActionsIncludes(EnvelopeAllovedActions.SIGN),
    haveApprove:
      checkAllowedActionsIncludes(EnvelopeAllovedActions.APPROVE)
  }
}

export const getAllowedAction = (checkedAllowedActions: ICheckedAllowedActions): EnvelopeAllowedActionsButton => {
  if (checkedAllowedActions.haveSign)
    return EnvelopeAllowedActionsButton.SIGN

  return (
    checkedAllowedActions.haveApprove ?
      EnvelopeAllowedActionsButton.APPROVE :
      EnvelopeAllowedActionsButton.VIEW
  )
}

type CurrentEnvelopeFile = NonNullable<ICurrentEnvelope['files']>[number]

const getEnvelopeTemplateFileOguid = (file: CurrentEnvelopeFile): string => (
  file.fileOguid ?? file.originalFileOguid ?? file.oguid ?? ''
)

const getDraftRecipientSigningParams = (
  recipient: ICurrentEnvelopeRecipient
) => (
  recipient.signingParams
    ? {
      ...recipient.signingParams,
      isSignOnBehalfOfOrganization: recipient.signingParams.isAllowElectronicSignature
        ? null
        : recipient.signingParams.isSignOnBehalfOfOrganization ??
          recipient.isSignOnBehalfOfOrganization ??
          false,
    }
    : {
      isAllowElectronicSignature: true,
      isSignOnBehalfOfOrganization: null,
      signerType: DraftEnvelopeRecipientSignerType.PART,
    }
)

const getDraftPrepositionedMark = (
  mark: IEnveloperPrepositionedMark,
  recipientUuid: string
): IDraftEnvelopePrepositionedMark => ({
  color: mark.color ?? null,
  fileIndex: mark.fileIndex,
  font: mark.font as IDraftEnvelopePrepositionedMark['font'],
  fontSize: mark.fontSize,
  fontType: mark.fontType as IDraftEnvelopePrepositionedMark['fontType'],
  height: mark.height,
  isReadOnly: mark.isReadOnly,
  isRequired: mark.isRequired,
  key: mark.key,
  label: mark.label ?? null,
  pageNumber: mark.pageNumber,
  recipient: recipientUuid,
  scale: mark.scale,
  tooltip: mark.tooltip,
  topLeftX: mark.topLeftX,
  topLeftY: mark.topLeftY,
  type: mark.type as IDraftEnvelopePrepositionedMark['type'],
  value: mark.value,
  valueList: mark.valueList,
  width: mark.width,
})

export const getDraftEnvelopeFromEnvelope = (
  envelope: ICurrentEnvelope
): IDraftEnvelope => {
  const files = (envelope.files ?? [])
    .map(getEnvelopeTemplateFileOguid)
    .filter(Boolean)

  const preview = (envelope.files ?? []).reduce<IDraftEnvelope['preview']>((acc, file) => {
    const fileOguid = getEnvelopeTemplateFileOguid(file)
    if (!fileOguid) return acc

    return {
      ...acc,
      [fileOguid]: {
        data: [],
        fileName: file.filename,
        isAvailable: true,
        total: file.pages ? Number(file.pages) : 0,
      }
    }
  }, {})

  const envelopeFields = envelope.fields ?? {}
  const fieldsIdList = Object.keys(envelopeFields)
  const fields = fieldsIdList.reduce<Record<string, IDraftEnvelopeField>>((acc, key) => ({
    ...acc,
    [key]: {
      key,
      value: envelopeFields[key],
    }
  }), {})

  const recipients: Record<string, IDraftEnvelopeRecipient> = {}
  const recipientsIdList: string[] = []
  const prepositionedMarks: Record<string, IDraftEnvelopePrepositionedMark> = {}
  const prepositionedMarksIdList: string[] = []
  let recipientsColors = [...DraftEnvelopeRecipientsBaseColors]

  envelope.flow.forEach((stage, stageIndex) => {
    stage.tasks.forEach((task, taskIndex) => {
      const uuid = `envelope-${stageIndex}-${taskIndex}`
      const [color, ...restColors] = recipientsColors
      const signer = task.recipient.declaredSigner
      const assignedToBoxUser = task.recipient.assignedToBoxUser
      const isAssignedToBoxUser = Boolean(assignedToBoxUser)

      recipientsColors = restColors
      recipientsIdList.push(uuid)

      recipients[uuid] = {
        assignmentDescription: null,
        assignedToBoxUserOguid: assignedToBoxUser?.oguid ?? null,
        boxEmail: task.recipient.email ?? assignedToBoxUser?.email ?? '',
        boxId: task.recipient.email ?? '',
        boxPhone: null,
        color: color ?? DraftEnvelopeRecipientsBaseColors[0],
        customParams: [],
        groupNum: stageIndex + 1,
        guestEmail: task.recipient.guestEmail,
        isAssignedToBoxUser,
        isHostMode: task.recipient.isHostMode ?? false,
        isRequireSelfie: task.recipient.isRequireSelfie,
        isRequireSms: task.recipient.isRequireSms,
        isSignOnBehalfOfOrganization: task.recipient.isSignOnBehalfOfOrganization ?? null,
        prepositionedMarks: null,
        recipientKey: task.recipient.recipientKey ?? null,
        signer: {
          fullName: signer?.fullName ?? assignedToBoxUser?.fullName ?? '',
          initials: signer?.initials ?? null,
          phone: signer?.phone ? String(signer.phone) : null,
          position: signer?.position ?? null,
        },
        signingParams: getDraftRecipientSigningParams(task.recipient),
        stageDescription: stage.description ?? null,
        stageKey: stage.stageKey ?? getAutoStageRouteKey(stageIndex),
        stageLabels: stage.labels?.map((label) => label.fullKey) ?? [],
        type: task.type as unknown as DraftEnvelopeRecipientType,
      }

      task.recipient.prepositionedMarks?.forEach((mark, markIndex) => {
        const markUuid = `${uuid}-mark-${markIndex}`

        prepositionedMarksIdList.push(markUuid)
        prepositionedMarks[markUuid] = getDraftPrepositionedMark(mark, uuid)
      })
    })
  })

  return {
    commentFiles: [],
    envelopeTemplate: null,
    fields,
    fieldsIdList,
    files,
    generatedTemplateFiles: null,
    generatedTemplateFilesSubject: null,
    labels: envelope.labels?.map((label) => label.fullKey) ?? [],
    notifications: envelope.notifications ?? null,
    message: {
      comment: envelope.comment,
      expirationDateTime: envelope.expirationDateTime
        ? new Date(envelope.expirationDateTime)
        : null,
      externalId: envelope.externalId,
      subject: envelope.subject,
    },
    preview,
    prepositionedMarks,
    prepositionedMarksIdList,
    recipients,
    recipientsColors,
    recipientsIdList,
    templateFiles: null,
    variablesIdList: [],
  }
}

export const getOptionsAllowedAction = (
  isCardEnvelope: boolean,
  checkedAllowedActions: ICheckedAllowedActions,
  canUseAsTemplate = false
): ILibDropdownItem[] => {
  const options: ILibDropdownItem[] = []

  if (!isCardEnvelope) {
    options
      .push({
        name: EnvelopeAllowedActionsName.OPEN,
        translation: 'common:open'
      })
  }

  if (
    checkedAllowedActions.haveDeclineSign ||
    checkedAllowedActions.haveDeclineApprove
  )
    options.push({
      name: EnvelopeAllowedActionsName.DECLINE,
      translation: 'common:decline',
      data: checkedAllowedActions.haveDeclineSign
        ? EnvelopeAllovedActions.DECLINE_SIGN
        : EnvelopeAllovedActions.DECLINE_APPROVE,
    });

  if (checkedAllowedActions.haveStopProcess)
    options
      .push({
        name: EnvelopeAllowedActionsName.STOP_PROCESS,
        translation: 'envelopes:actions:stopProcess'
      })

  if (canUseAsTemplate)
    options
      .push({
        name: EnvelopeAllowedActionsName.USE_AS_TEMPLATE,
        translation: 'envelopes:actions:useAsTemplate'
      })

  return options
}
